import { type FilterType } from '../../types/beFiltersApi'
import {
  DateFormat,
  DateMatch,
  DateTimeFormat,
  DateTimeMatch,
  EmailMatch,
  NumericFormat,
  NumericMatch,
  SmsMatch,
  TextMatch,
  type TrackingMatch,
  type ActionType,
} from '../../types/FilterCondition'

export interface UiFilterDetail {
  IdList: number
  Name: string
  Description: string
  Notes?: string
  CustomDescription?: boolean
  Hidden: boolean
  Category: number
  UpdateDate: string
  Type: FilterType
  Blocks: UiBlock[]
  Id: number
  Locked: boolean
  IsUsedInOtherLists?: boolean
}
export interface UiBlock {
  JoinType: ActionType
  Conditions: UiFilterCondition[]
}

export interface UiFilterCondition {
  JoinType: ActionType
  ConditionType?: UiConditionType
  Spec?: Partial<UiGenericSpec>
}

export enum UiProfileConditionTypes {
  ProfileField = 'ProfileField',
  GroupBelongs = 'GroupBelongs',
  GroupNotBelongs = 'GroupNotBelongs',
}

export enum UiEngagementConditionTypes {
  Engagement = 'Engagement',
}

export enum UiGeoConditionTypes {
  GeoLocatedIn = 'GeoLocatedIn',
  GeoNotLocatedIn = 'GeoNotLocatedIn',
}

export enum UiDeviceConditionTypes {
  Device = 'Device',
}

export enum UiTrackingConditionTypes {
  TrackingActive = 'TrackingActive',
  TrackingAnonymous = 'TrackingAnonymous',
}

export enum UiSubscriptionConditionType {
  EmailSubscribed = 'EmailSubscribed',
  EmailUnsubscribed = 'EmailUnsubscribed',
}

export const UiActivityConditionType = {
  ...SmsMatch,
  ...EmailMatch,
  ...UiSubscriptionConditionType,
}
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type UiActivityConditionType =
  | SmsMatch
  | EmailMatch
  | UiSubscriptionConditionType

export const UiConditionType = {
  ...UiProfileConditionTypes,
  ...UiActivityConditionType,
  ...UiEngagementConditionTypes,
  ...UiGeoConditionTypes,
  ...UiDeviceConditionTypes,
  ...UiTrackingConditionTypes,
}
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type UiConditionType =
  | UiProfileConditionTypes
  | UiActivityConditionType
  | UiEngagementConditionTypes
  | UiGeoConditionTypes
  | UiDeviceConditionTypes
  | UiTrackingConditionTypes

export type UiGenericSpec =
  | UiGroupSpec
  | UiProfileSpec
  | UiEngagementSpec
  | UiGeoSpec
  | UiDeviceSpec
  | UiActivitySpec
  | UiTrackingSpec

export type DeviceType =
  | 'SysSpec_Symbian'
  | 'SysSpec_MS_Windows_Phone'
  | 'SysSpec_MS_Windows'
  | 'SysSpec_Linux'
  | 'SysSpec_BlackBerry'
  | 'SysSpec_Apple_Mac_OS'
  | 'SysSpec_Apple_iOS'
  | 'SysSpec_Android'
  | 'DeviceSpec_Windows_Phone'
  | 'DeviceSpec_Symbian'
  | 'DeviceSpec_PC_Windows'
  | 'DeviceSpec_PC_Linux'
  | 'DeviceSpec_PC_Apple'
  | 'DeviceSpec_iPhone'
  | 'DeviceSpec_iPad'
  | 'DeviceSpec_BlackBerry'
  | 'DeviceSpec_Android'

export interface TrackingDateRangeValue {
  From: Date | null
  To: Date | null
}

export interface UiDeviceSpec {
  Value?: DeviceType
}

export interface UiTrackingSpec {
  Identifier: string
  TrackingActive: boolean
  TrackingMatchOperator?: TrackingMatch
  Dates?: {
    Type: 'DateRangeDetails'
    Details: TrackingDateRangeValue
  }
}

export interface UiGeoSpec {
  GeoMatchOperator: 'LocatedIn' | 'NotLocatedIn'
  Country: string
  Region: string
  Precision: number
}

export interface UiEngagementSpec {
  Value?:
    | 'Bounce'
    | 'New'
    | 'Inactive'
    | 'LessActive'
    | 'Active'
    | 'VeryActive'
    | 'Abandoned'
}

export interface UiGroupSpec {
  Values: number[]
}
export type UiProfileSpec =
  | UiTextSpec
  | UiNumericSpec
  | UiDateSpec
  | UiDateTimeSpec

export interface UiTextSpec {
  Identifier: string
  Format: UiTextFormat
  Match: TextMatch
  Value?: string
}

export interface UiNumericSpec {
  Identifier: string
  Format: NumericFormat
  Match: NumericMatch
  Value: number
}

export interface UiDateSpec {
  Identifier: string
  Format: DateFormat
  Match: DateMatch
  Value?: number | string
}

export interface UiDateTimeSpec {
  Identifier: string
  Format: DateTimeFormat
  Match: DateTimeMatch
  Value: number
}

export enum UiTextFormat {
  Text = 'Text',
}
export const UiFormat = {
  ...UiTextFormat,
  ...NumericFormat,
  ...DateFormat,
  ...DateTimeFormat,
}
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type UiFormat =
  | UiTextFormat
  | NumericFormat
  | DateFormat
  | DateTimeFormat

export const UiMatch = {
  ...TextMatch,
  ...NumericMatch,
  ...DateMatch,
  ...DateTimeMatch,
}
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type UiMatch = TextMatch | NumericMatch | DateMatch | DateTimeMatch

export enum UiActivityDateType {
  AnyMoment = 'AnyMoment',
  DateInterval = 'DateInterval',
  LastDays = 'LastDays',
  LastHours = 'LastHours',
  ExactDaysAgo = 'ExactDaysAgo',
  ExactHoursAgo = 'ExactHoursAgo',
}

export interface UiActivityDateRangeValue {
  From: Date | null
  To: Date | null
}
export type UiActivityDateValue = null | number | UiActivityDateRangeValue

export interface UiActivitySubsSpec {
  DateType: UiActivityDateType
  DateValue: UiActivityDateValue
}

export enum UiChannelType {
  Email = 'Email',
  Sms = 'Sms',
}

export type UiActivityMessageSpec = UiActivitySubsSpec & {
  Message: number
  ChannelType: UiChannelType
  Tags: number[]
}

export type UiActivityMessageLinkSpec = UiActivityMessageSpec & {
  Link: string
}

export type UiActivitySpec =
  | UiActivitySubsSpec
  | UiActivityMessageSpec
  | UiActivityMessageLinkSpec
